import type { MeResponse, TokenPair } from '@facecontrol/shared';
import { ApiError, apiFetch } from './http';
import { getTgEnv } from './tg';

export interface Session {
  tokens: TokenPair;
  me: MeResponse;
}

const KEY = 'fc.session.v2';
/** Inside Telegram the session lives for the launch; on the desktop site it survives the tab (logout clears it). */
const store = (): Storage => (getTgEnv().inTelegram ? sessionStorage : localStorage);

export function loadStoredSession(): Session | null {
  try {
    const raw = store().getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function storeSession(s: Session | null) {
  try {
    if (s) store().setItem(KEY, JSON.stringify(s));
    else store().removeItem(KEY);
  } catch {
    /* private mode etc. */
  }
}

/** Exchange Telegram initData for tokens. Nothing else is awaited before first render. */
export async function login(initDataRaw: string): Promise<Session> {
  const r = await apiFetch<TokenPair & { me: MeResponse }>('/api/auth/token/create', { method: 'POST', body: { initDataRaw } });
  const s = { tokens: { accessToken: r.accessToken, refreshToken: r.refreshToken }, me: r.me };
  storeSession(s);
  return s;
}

export async function refresh(s: Session): Promise<Session> {
  const r = await apiFetch<TokenPair & { me: MeResponse }>('/api/auth/token/refresh', { method: 'POST', body: { refreshToken: s.tokens.refreshToken } });
  const next = { tokens: { accessToken: r.accessToken, refreshToken: r.refreshToken }, me: r.me };
  storeSession(next);
  return next;
}

/** Authenticated call with one transparent refresh on 401. */
export async function withAuth<T>(get: () => Session, set: (s: Session) => void, fn: (token: string) => Promise<T>): Promise<T> {
  try {
    return await fn(get().tokens.accessToken);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      const next = await refresh(get());
      set(next);
      return fn(next.tokens.accessToken);
    }
    throw e;
  }
}

/** Desktop site: the session handed over after «Да, это я» in the bot. */
export function adoptWebSession(r: TokenPair & { me: MeResponse }): Session {
  const s = { tokens: { accessToken: r.accessToken, refreshToken: r.refreshToken }, me: r.me };
  storeSession(s);
  return s;
}

export async function logout(s: Session | null): Promise<void> {
  storeSession(null);
  if (s) await apiFetch<void>('/api/auth/logout', { method: 'DELETE', token: s.tokens.accessToken, timeoutMs: 5000 }).catch(() => undefined);
}
